import {
  Card,
  CardHeader,
  Divider,
  CardBody,
  Image,
  Tooltip,
  Chip,
  Skeleton,
  Button,
} from "@heroui/react";
import { ProductObject } from "app/api/product/product.type";
import { formatToTwoDecimalPlaces, toKebabCase } from "app/utils/fn";
import { Link, Link as RouterLink } from "@remix-run/react";
import { useState, useRef } from "react";
import {
  FiEye,
  FiShoppingCart,
  FiHeart,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUpRight,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { ProductCollectionCard } from "app/components/Product/ProductCollectionCard";
import { productPreviewAtom, isProductPreviewOpen } from "app/atoms/product.atom";
import { useSetAtom } from "jotai";
import { MdChevronRight, MdOutlineChevronRight } from "react-icons/md";

export type MixedSectionProps = {
  _id?: string;
  title?: string;
  slug?: string;
  subTitle?: string;
  order?: number;
  products: ProductObject[];
  total: number;
  isLoading?: boolean;
};

export default function MixedSection({
  _id,
  title,
  slug,
  subTitle,
  order,
  products,
  total,
  isLoading = false,
}: MixedSectionProps) {
  // State for collapsible functionality
  const [isExpanded, setIsExpanded] = useState(false);

  // Product preview functionality
  const setProduct = useSetAtom(productPreviewAtom);
  const setIsProductPreviewOpen = useSetAtom(isProductPreviewOpen);

  const handleProductPreview = (product: ProductObject) => {
    // Convert ProductObject to ProductCardProps for preview
    const previewProps = {
      image: product.overview?.heroImage || "",
      images: product.product?.images || [],
      productCode: product.overview?.code || "",
      title: product.overview?.name || "",
      labels: product.labels || [],
      description: product.product?.description || "",
      userTypePrice: product.userTypePrice,
      qty: product.quantity || { min: 0, max: 0 },
      category: product.category?.name || "",
      subCatgory: product.subCategory?.name || "",
      slug: product.slug || "",
      isSpecialSupplier: product.supplier?.isSpecialSupplier || false,
    };
    setProduct(previewProps);
    setIsProductPreviewOpen(true);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card
      as="section"
      aria-labelledby={`mixed-section-${order}`}
      className="w-full shadow-none bg-white/80 dark:bg-gray-800/80 backdrop-blur-md  border border-white/20 dark:border-gray-700/50"
    >
      {/* Header Section */}
      {(title || subTitle) && (
        <CardHeader className="pb-4 justify-between items-center">
          <div className="w-fit text-center">
            {title && (
              <Link
                to={slug ? `/promotionals/${slug}` : `/promotionals/${toKebabCase(title)}`}
                prefetch="intent"
              >
                <h2
                  id={`mixed-section-${order}`}
                  className="text-2xl md:text-3xl font-bold text-primary dark:text-white mb-2 capitalize"
                >
                  {title}
                </h2>
              </Link>
            )}
            {subTitle && (
              <p className="text-base text-gray-600 dark:text-gray-300 capitalize">{subTitle}</p>
            )}
          </div>
          {title && (
            <div className="flex justify-center mt-6">
              <Link
                to={slug ? `/promotionals/${slug}` : `/promotionals/${toKebabCase(title)}`}
                color="primary"
                prefetch="intent"
                className="min-w-fit bg-primary-50 text-primary-800 font-medium px-4 py-2 rounded-lg flex gap-x-3"
              >
                View All
                <MdChevronRight className="text-lg" />
              </Link>
            </div>
          )}
        </CardHeader>
      )}

      {(title || subTitle) && <Divider />}

      {/* Products Display Section */}
      {products && products.length > 0 && (
        <CardBody className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {products.map((product, index) => {
              // Define different gradient themes for variety
              const themes = [
                { from: "from-blue-100", to: "to-blue-200", icon: "🛍️" },
                { from: "from-green-100", to: "to-green-200", icon: "✨" },
                { from: "from-purple-100", to: "to-purple-200", icon: "🎯" },
                { from: "from-pink-100", to: "to-pink-200", icon: "💎" },
              ];

              const theme = themes[index % themes.length];

              return (
                <ProductCollectionCard
                  key={product._id}
                  product={product}
                  gradientFrom={theme.from}
                  gradientTo={theme.to}
                  icon={theme.icon}
                  buttonText="View Product"
                  onPreview={handleProductPreview}
                />
              );
            })}
          </div>

        </CardBody>
      )}
    </Card>
  );
}
